import { useState, useEffect, useRef, useCallback } from "react";
import { pubSub } from "../../ably";
import { CHANNELS } from "../../channel/channels.js";
import { addAdminChatComment, setCmeCommentPinned, updateCmeCommentStatus, useAdminUserDetails } from "../../api/adminHook.js";
import { buildModeratorClientId } from "../../utils/moderatorIdentity.js";
import { normalizeChatMessage, normalizeChatSentList } from "../../channel/normalizePayload.js";
import { applyKeyedListMessage, upsertMerge } from "../../realtime/channelSyncEngine.js";
import useTotalUsers from "../../hooks/useTotalUsers.js";

function chatItemId(item) {
  return String(item?.messageId);
}

// At most this many messages pinned at once (the viewer chat's pinned bar).
export const MAX_PINNED_MESSAGES = 3;

// Shape published on the chat-sent channel: only approved messages travel to
// viewers, and every entry carries an explicit boolean `pinned` so a pin
// toggle round-trips through the channel (missing key = unpinned).
// Admin replies also carry `replyTo` (a snapshot of the quoted message). If the
// quoted message has since been un-approved, the quote is dropped so its text
// never reaches viewers through the reply.
function toSentPayload(list) {
  const unapprovedIds = new Set(list.filter((m) => !m.isApproved).map((m) => m.messageId));
  return list
    .filter((m) => m.isApproved)
    .map(({ messageId, userId, name, image, sessionId, message, createdAt, pinned, comment_id, replyTo }) => ({
      messageId,
      userId,
      name,
      image,
      sessionId,
      // Every message's DB id: an HCP message's from sessions/submit/session/comment, an
      // admin message's from cme/comment/add. Kept on chat-sent so the copy that comes back
      // never loses it (a reply quotes it from there). Absent on messages from before it.
      ...(comment_id != null ? { comment_id } : {}),
      message,
      isApproved: true,
      pinned: pinned === true,
      createdAt,
      ...(replyTo && !unapprovedIds.has(replyTo.messageId) ? { replyTo } : {}),
    }));
}

// The API answered a failure: HTTP errors already reject in apiClient; this catches a 2xx
// whose body says otherwise - a status other than "success" (cme/comment/add answers
// { status: "success", msg, data: { commentId } }) or a status_code outside 2xx.
function isApiSuccess(res) {
  if (typeof res?.status === "string" && res.status.toLowerCase() !== "success") return false;
  const code = res?.status_code;
  return code == null || (Number(code) >= 200 && Number(code) < 300);
}

// upsertMerge, but a copy without comment_id (a chat-sent snapshot from before it was
// carried there) never wipes the one already known from chat-recive.
function mergeKeepingCommentId(current, items, getId) {
  const known = new Map((current || []).filter((m) => m.comment_id != null).map((m) => [getId(m), m.comment_id]));
  const kept = items.map((m) => (m.comment_id == null && known.has(getId(m)) ? { ...m, comment_id: known.get(getId(m)) } : m));
  return upsertMerge(current, kept, getId);
}

// showMsg(text, type): the session's toast (useSessionState) - optional.
export default function useChatModeration(session, _activeTab, showMsg) {
  const [chatMessages, setChatMessages] = useState([]);
  // Live viewer count: same presence source as useTotalUsers / the Users modal.
  const chatOnline = useTotalUsers();
  const [chatInput, setChatInput] = useState("");
  const [chatReady, setChatReady] = useState(false);
  // messageId the admin is replying to (null = a plain message).
  const [replyTargetId, setReplyTargetId] = useState(null);
  // An admin message is waiting on cme/comment/add (the composer is locked meanwhile).
  const [chatSending, setChatSending] = useState(false);
  const sendingRef = useRef(false);
  const chatMessagesRef = useRef(null);
  const listRef = useRef([]);

  const sessionId = session?.sessionId;
  
  const { data: adminUser } = useAdminUserDetails();
  const adminName = (() => {
    let realName = adminUser?.name || adminUser?.first_name || adminUser?.data?.name || adminUser?.data?.first_name;
    if (!realName && adminUser?.firstName) {
      realName = [adminUser.prefix, adminUser.firstName, adminUser.lastName].filter(Boolean).join(' ').trim();
    } else if (!realName && adminUser?.data?.firstName) {
      realName = [adminUser.data.prefix, adminUser.data.firstName, adminUser.data.lastName].filter(Boolean).join(' ').trim();
    }
    return realName || "Admin";
  })();
  const adminImage = adminUser?.profileImage || adminUser?.image || adminUser?.data?.image || null;

  const setList = useCallback((next) => {
    listRef.current = next;
    setChatMessages(next);
  }, []);

  useEffect(() => {
    if (!session || !sessionId) return;
    let mounted = true;
    const cacheKey = ["admin-chat", Number(sessionId)];
    const config = {
      getId: chatItemId,
      classify(raw) {
        const msg = normalizeChatMessage(raw);
        return msg ? { kind: "items", items: [{ ...msg, isApproved: false }] } : { kind: "ignore", items: [] };
      },
      getCurrent: () => listRef.current,
      setResult: setList,
      merge: mergeKeepingCommentId,
    };
    const onChat = (data, _isHistory, meta = {}) => {
      if (!mounted) return;
      try {
        applyKeyedListMessage({ cacheKey, config, message: { id: meta.id, timestamp: meta.timestamp, raw: data } });
      } catch (e) {
        console.warn("[useChatModeration] onChat failed", e);
      }
    };
    pubSub.subscribe(CHANNELS.CHAT, onChat);
    setChatReady(true);
    return () => {
      mounted = false;
      pubSub.unsubscribe(CHANNELS.CHAT, onChat);
      // Readiness must track the subscription: without this the UI kept a
      // stale "ready" state after the session dropped and never reconnected.
      setChatReady(false);
    };
  }, [session, sessionId, setList]);

  useEffect(() => {
    if (!session || !sessionId) return;
    let mounted = true;
    const cacheKey = ["admin-chat", Number(sessionId)];
    const config = {
      getId: chatItemId,
      classify(raw) {
        const list = normalizeChatSentList(raw);
        return list && list.length ? { kind: "items", items: list } : { kind: "ignore", items: [] };
      },
      getCurrent: () => listRef.current,
      setResult: setList,
      merge: mergeKeepingCommentId,
    };
    const onSent = (data, _isHistory, meta = {}) => {
      if (!mounted) return;
      try {
        applyKeyedListMessage({ cacheKey, config, message: { id: meta.id, timestamp: meta.timestamp, raw: data } });
      } catch (e) {
        console.warn("[useChatModeration] onSent failed", e);
      }
    };
    pubSub.subscribe(CHANNELS.CHAT_SENT, onSent);
    return () => {
      mounted = false;
      pubSub.unsubscribe(CHANNELS.CHAT_SENT, onSent);
    };
  }, [session, sessionId, setList]);

  // Seed the list from channel history. The Ably layer replays history exactly
  // once, right after the first connect — anything mounted later (the devconfig
  // Chat tab mounts when it is opened) would stay empty until the next live
  // publish. Re-reading on mount + reconnect keeps the cache deterministic.
  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    const cacheKey = ["admin-chat", Number(sessionId)];
    const configFor = (classify) => ({
      getId: chatItemId,
      classify,
      getCurrent: () => listRef.current,
      setResult: setList,
      merge: mergeKeepingCommentId,
    });

    const seedChannel = async (channel, classify) => {
      try {
        const items = await pubSub.getChannelHistoryWithMeta(channel, { limit: 50, maxPages: 1 });
        if (cancelled || !items?.length) return;
        // Ably hands history back newest-first; replay oldest-first so the
        // per-item freshness check inside applyKeyedListMessage only moves forward.
        [...items].reverse().forEach((item) => {
          try {
            applyKeyedListMessage({
              cacheKey,
              config: configFor(classify),
              message: { id: item.id, timestamp: item.timestamp, raw: item.data },
            });
          } catch (e) {
            console.warn("[useChatModeration] history apply failed", e);
          }
        });
      } catch (e) {
        console.warn("[useChatModeration] history seed failed", e);
      }
    };

    const seed = () => {
      seedChannel(CHANNELS.CHAT, (raw) => {
        const msg = normalizeChatMessage(raw);
        return msg ? { kind: "items", items: [{ ...msg, isApproved: false }] } : { kind: "ignore", items: [] };
      });
      seedChannel(CHANNELS.CHAT_SENT, (raw) => {
        const list = normalizeChatSentList(raw);
        return list && list.length ? { kind: "items", items: list } : { kind: "ignore", items: [] };
      });
    };

    if (pubSub.isConnected) seed();
    const onConn = (connected) => {
      if (connected) seed();
    };
    pubSub.onConnectionChange(onConn);
    return () => {
      cancelled = true;
      pubSub.offConnectionChange(onConn);
    };
  }, [sessionId, setList]);

  // Like Pin, a reply is only offered on a message that is approved (live for
  // viewers) - an un-approved target simply stops being a reply target.
  const replyingTo = chatMessages.find((m) => m.messageId === replyTargetId && m.isApproved) || null;

  function startReply(messageId) {
    const target = listRef.current.find((m) => m.messageId === messageId);
    if (!target?.isApproved) return;
    setReplyTargetId(messageId);
  }

  function cancelReply() {
    setReplyTargetId(null);
  }

  // Logs an admin message's text with cme/comment/add (the fixed payload - see
  // addAdminChatComment) and resolves the answer's data.commentId, which becomes the
  // message's comment_id on chat-sent. Throws when the API failed or answered without a
  // commentId: the text is then not known to be logged, so it must not reach viewers.
  // parentId: the comment_id of the HCP message replied to, 0 for a plain message (or a
  // reply to a message from before comment_id).
  async function logAdminComment({ parentId, message }) {
    const res = await addAdminChatComment({ parentId: parentId ?? 0, sessionId: String(sessionId), message });
    if (!isApiSuccess(res)) throw new Error(res?.msg || res?.message || `status ${res?.status ?? res?.status_code}`);
    const commentId = res?.data?.commentId;
    if (commentId == null || commentId === "") throw new Error("comment add returned no commentId");
    return commentId;
  }

  // The DB first, Ably second: an admin message reaches viewers only once cme/comment/add
  // has logged it. Resolves true when sent; false when not - the API failed (nothing was
  // published, the typed text and the reply target stay for another try) or the message
  // could not be addressed.
  async function sendChatMsg() {
    if (!chatInput.trim() || sendingRef.current) return false;
    if (!sessionId) {
      // No session: nothing can be addressed (userId/sessionId would be
      // "undefined"), so the message is neither stored nor published.
      console.warn("[admin-chat] no session id — message not sent");
      return false;
    }
    // The moderator's Ably clientId, e.g. "moderator-23806-274" - never a shared "admin".
    const userId = buildModeratorClientId(sessionId, adminUser);
    if (!userId) {
      console.warn("[admin-chat] no moderator id from user details — message not sent");
      return false;
    }
    const comment = chatInput.trim();
    const target = replyingTo;

    let commentId;
    sendingRef.current = true;
    setChatSending(true);
    try {
      commentId = await logAdminComment({ parentId: target?.comment_id, message: comment });
    } catch (e) {
      console.warn("[admin-chat] comment add failed - not sent to chat:", e?.message || e);
      return false;
    } finally {
      sendingRef.current = false;
      setChatSending(false);
    }

    const newMsg = {
      messageId: "admin_msg_" + Date.now(),
      userId,
      name: adminName,
      image: adminImage,
      sessionId: String(sessionId),
      comment_id: commentId,
      message: comment,
      createdAt: new Date().toISOString(),
      isApproved: true,
      pinned: false,
      ...(target
        ? {
            replyTo: {
              messageId: target.messageId,
              userId: target.userId || "",
              name: target.name || "",
              message: target.message || "",
              // The quoted HCP message's comment API id (absent on messages from before it).
              ...(target.comment_id != null ? { comment_id: target.comment_id } : {}),
            },
          }
        : {}),
    };
    // listRef.current, not a copy from before the API call: messages that arrived or were
    // moderated meanwhile stay.
    const next = [...listRef.current, newMsg];
    setList(next);
    setChatInput("");
    setReplyTargetId(null);
    pubSub.publish(CHANNELS.CHAT_SENT, toSentPayload(next));
    return true;
  }

  // Unapproving also unpins: a message viewers can't see can't stay pinned, and approving
  // it again brings it back unpinned.
  function approveMessage(messageId, isApproved) {
    const next = listRef.current.map((m) =>
      m.messageId === messageId
        ? isApproved
          ? { ...m, isApproved, approvedBy: "Admin", approvedAt: Date.now() }
          : { ...m, isApproved, pinned: false }
        : m
    );
    setList(next);
    pubSub.publish(CHANNELS.CHAT_SENT, toSentPayload(next));
    if (String(messageId).startsWith("admin_msg_")) return;
    // The status API identifies the comment by the comment API's id - comment_id, sent with
    // the HCP's message - never by the Ably messageId. A message without one (sent before
    // it was added) has no comment id to report: only the channel state changes.
    const target = next.find((m) => m.messageId === messageId);
    if (target?.comment_id == null) {
      console.warn("[admin-chat] no comment_id on message - status not reported:", messageId);
      return;
    }
    updateCmeCommentStatus(target.comment_id, isApproved ? 1 : 0).catch((e) =>
      console.warn("[admin-chat] update failed:", e?.message || e)
    );
  }

  // Pinning: the approved list is republished with the new `pinned` flag so viewers can show
  // the message as sticky, and the pin is recorded in the DB (cme/comment/pinned) under the
  // message's own comment_id - the HCP's for an HCP message, the admin's for an admin one.
  // The approval status API is untouched.
  // Returns false (and changes nothing) when pinning would go past
  // MAX_PINNED_MESSAGES; only approved messages count - those are the pins
  // viewers see. Unpinning is always allowed.
  function togglePinned(messageId, pinned) {
    if (pinned === true) {
      const pinnedCount = listRef.current.filter(
        (m) => m.isApproved && m.pinned === true && m.messageId !== messageId
      ).length;
      if (pinnedCount >= MAX_PINNED_MESSAGES) return false;
    }
    const next = listRef.current.map((m) =>
      m.messageId === messageId ? { ...m, pinned: pinned === true } : m
    );
    setList(next);
    pubSub.publish(CHANNELS.CHAT_SENT, toSentPayload(next));
    // A message without comment_id (from before it was added) has no DB comment to update.
    // Either way the admin is told when the DB was not updated - a console warning alone
    // made a skipped or failed call look like the API was never wired up.
    const action = pinned === true ? "Pin" : "Unpin";
    const target = next.find((m) => m.messageId === messageId);
    if (target?.comment_id == null) {
      console.warn("[admin-chat] no comment_id on message - pin not recorded:", messageId);
      showMsg?.(`${action} shown to viewers but not saved: this message has no comment ID (sent before IDs were added).`, "error");
    } else {
      setCmeCommentPinned(target.comment_id, pinned === true)
        .then((res) => {
          if (!isApiSuccess(res)) throw new Error(res?.msg || res?.message || `status ${res?.status ?? res?.status_code}`);
        })
        .catch((e) => {
          console.warn("[admin-chat] pin update failed:", e?.message || e);
          showMsg?.(`${action} not saved (${e?.message || "request failed"}) - it still shows for viewers.`, "error");
        });
    }
    return true;
  }

  // This admin's moderator id - messages carrying it are theirs (and editable).
  const adminUserId = buildModeratorClientId(sessionId, adminUser);

  // An admin corrects one of their own messages - a plain one or a reply. Like sending, the
  // DB first: the new text is logged with cme/comment/add (same fixed payload; parentId the
  // comment_id of the HCP message a reply answers, else 0), and only once that succeeds is
  // the text replaced and the approved list republished, so viewers (and a reload, which
  // replays chat-sent) see it. The answer's commentId becomes the message's comment_id.
  // Resolves true when saved (or the text is unchanged), false when refused (an empty text
  // or a message that isn't this admin's - nothing happens), "failed" when the API failed
  // (nothing was published; the old text stays).
  async function editMessage(messageId, text) {
    const message = String(text ?? "").trim();
    const target = listRef.current.find((m) => m.messageId === messageId);
    if (!message || !adminUserId || !target || target.userId !== adminUserId) return false;
    if (target.message === message) return true;
    let commentId;
    try {
      commentId = await logAdminComment({ parentId: target.replyTo?.comment_id, message });
    } catch (e) {
      console.warn("[admin-chat] comment add failed - edit not sent to chat:", e?.message || e);
      return "failed";
    }
    // listRef.current, not a copy from before the API call: messages that arrived or were
    // moderated meanwhile stay.
    const next = listRef.current.map((m) => (m.messageId === messageId ? { ...m, message, comment_id: commentId } : m));
    setList(next);
    pubSub.publish(CHANNELS.CHAT_SENT, toSentPayload(next));
    return true;
  }

  return {
    chatReady,
    chatMessages,
    chatOnline,
    chatInput,
    setChatInput,
    chatMessagesRef,
    sendChatMsg,
    chatSending,
    approveMessage,
    togglePinned,
    replyingTo,
    startReply,
    cancelReply,
    adminUserId,
    editMessage,
  };
}
